import {ReactNode} from 'react'
import {ErrorBox} from '@components/commons/error-box'
import {AlertTriangleIcon, ArrowRightIcon, ChevronDownIcon, ChevronUpIcon} from '@components/ui/icon'
import {DataTable} from '@components/commons/data-table'
import {TableBodyCellText} from '@components/ui/table-atoms'
import {ListingTableCellInfo} from '@features/home/components/listing-table-cell-info'
import {Button} from '@components/ui/button'
import {generatePath, useLocation} from 'react-router-dom'
import {routes} from '@utilities/constants/routes.tsx'
import {Trans, useTranslation} from '@/translations/i18n.tsx'
import {useParsedSearchParams} from '@hooks/useParsedSearchParams.ts'
import {AssignmentsURLParams} from '@features/home/types.ts'
import {useHomeAssignments} from '@features/home/queries/useHomeAssignments.ts'
import {formatCurrency} from '@utilities/helpers.ts'
import {formatLocaleDate} from '@/dates.ts'
import {HomeTableEmptyState} from '@features/home/components/home-table-empty-state'
import {HomeTableEmptySearch} from '@features/home/components/home-table-empty-search'
import {MobileTable} from '@components/commons/mobile-table.tsx'
import {PaymentsExpandedRows, PrimaryMobileTableRow} from '@features/home/components/home-atoms'
import {renderStatusFromAssignment, TABLE_ELEMENT_LIMIT} from '@features/home/utils.tsx'
import {ButtonLink} from '@components/commons/button-link'
import {useMediaQuery} from '@hooks/use-media-query'
import {UPWORK_URL} from '@features/payments/constants'
import {TASK_CATEGORY_REVIEW} from '@features/assignment/utils.tsx'

const OpenAssignmentLink = ({
    code,
    search,
    children
}: {
    code: number | string
    search?: string
    children?: ReactNode
}) => {
    const {t} = useTranslation()

    return (
        <ButtonLink
            to={{
                pathname: generatePath(routes.ASSIGNMENT.handle.fullPath, {code: code.toString()}),
                search
            }}
            size={'sm'}
            variant={'tertiary'}
        >
            {children ?? t('commons:open')}
        </ButtonLink>
    )
}

const CANCELLED_OR_DECLINED_STATUSES = ['auto_cancelled', 'declined', 'hard_cancelled', 'soft_cancelled'] as const

const GoToUpworkLink = () => {
    const {t} = useTranslation()

    return (
        <ButtonLink to={UPWORK_URL} target="_blank" rel="noopener noreferrer" variant="secondary" size="sm">
            {t('assignment:go_to_upwork_cta')}
        </ButtonLink>
    )
}

export const CompletedTable = ({onClearSearch}: {onClearSearch: () => void}) => {
    const {t} = useTranslation()
    const {search} = useLocation()
    const {searchParams, setSearchParams} = useParsedSearchParams(AssignmentsURLParams)
    const assignmentsQuery = useHomeAssignments(searchParams)
    const isLarge = useMediaQuery({breakpoint: 'md'})

    const showHideCanceledToggle = () => {
        setSearchParams({hide_cancelled: searchParams.hide_cancelled == 'true' ? 'false' : 'true'})
    }

    return (
        <div className="flex flex-col gap-4 w-full items-stretch">
            <div className="flex w-full items-start justify-between gap-2 flex-col md:flex-row">
                <p>{t('home:table:titles:assignments_all_completed')}</p>
                <Button variant={'tertiary'} onClick={showHideCanceledToggle} shape={'text'}>
                    {searchParams.hide_cancelled == 'true' ? t('home:show_canceled') : t('home:hide_canceled')}
                </Button>
            </div>
            {assignmentsQuery.isError ? (
                <ErrorBox title={t('errors:default')} icon={<AlertTriangleIcon />} />
            ) : assignmentsQuery.data?.data.length == 0 && !searchParams.search ? (
                <HomeTableEmptyState />
            ) : !isLarge ? (
                <MobileTable
                    pagination={{
                        ...assignmentsQuery.persistedPagination,
                        currentPage: searchParams.page,
                        limit: TABLE_ELEMENT_LIMIT,
                        onChangePage: page => setSearchParams({page})
                    }}
                    emptyStateComponent={<HomeTableEmptySearch onClearSearch={onClearSearch} />}
                    isLoading={assignmentsQuery.isFetching}
                    data={assignmentsQuery.data?.data ?? []}
                    expandedRow={dataItem => <PaymentsExpandedRows assignment={dataItem} />}
                    rows={[
                        {
                            id: 'listing',
                            cell: ({dataItem, isRowExpanded, toggleExpanded}) => {
                                const isUpworkPayment = dataItem.is_paid_via_upwork
                                const isCompleted = dataItem.task_status.status == 'completed'
                                const isReview = dataItem.task_category.name == TASK_CATEGORY_REVIEW
                                const isCancelledOrDeclined = CANCELLED_OR_DECLINED_STATUSES.some(
                                    status => status == dataItem.status
                                )
                                const hideOpen = isCancelledOrDeclined || (isCompleted && !isReview)
                                const paymentsCount = dataItem.payments?.length ?? 0

                                const renderCta = () => {
                                    if (isUpworkPayment) {
                                        if (isCompleted) {
                                            return <GoToUpworkLink />
                                        }
                                        return hideOpen ? null : (
                                            <OpenAssignmentLink code={dataItem.code}>
                                                <ArrowRightIcon />
                                            </OpenAssignmentLink>
                                        )
                                    }

                                    if (paymentsCount > 0) {
                                        return (
                                            <Button onClick={toggleExpanded} size={'sm'} variant={'tertiary'}>
                                                {isRowExpanded ? (
                                                    <ChevronUpIcon size={20} />
                                                ) : (
                                                    <ChevronDownIcon size={20} />
                                                )}
                                            </Button>
                                        )
                                    }

                                    if (hideOpen) {
                                        return null
                                    }

                                    return (
                                        <OpenAssignmentLink code={dataItem.code}>
                                            <ArrowRightIcon />
                                        </OpenAssignmentLink>
                                    )
                                }

                                return (
                                    <PrimaryMobileTableRow>
                                        <div className="flex flex-col gap-2">
                                            <ListingTableCellInfo item={dataItem} />
                                            {renderStatusFromAssignment(dataItem)}
                                        </div>
                                        {renderCta()}
                                    </PrimaryMobileTableRow>
                                )
                            },
                            header: () => <></>
                        },
                        {
                            cell: ({dataItem}) => (
                                <TableBodyCellText>
                                    {dataItem.completed_at
                                        ? formatLocaleDate(dataItem.completed_at, 'yyyy-MM-dd')
                                        : '-'}
                                </TableBodyCellText>
                            ),
                            header: () => (
                                <TableBodyCellText variant={'normal'}>{t('commons:completed_at')}</TableBodyCellText>
                            ),
                            id: 'completed_at'
                        },
                        {
                            cell: ({dataItem}) => {
                                const isUpworkPayment = dataItem.is_paid_via_upwork
                                if (isUpworkPayment) {
                                    return (
                                        <TableBodyCellText variant={'dark'}>
                                            <Trans i18nKey="assignment:upwork_payment" components={{br: <br />}} />
                                        </TableBodyCellText>
                                    )
                                }
                                return (
                                    <TableBodyCellText>
                                        {dataItem.total_amount ? formatCurrency(dataItem.total_amount, 'USD') : '-'}
                                    </TableBodyCellText>
                                )
                            },
                            header: () => (
                                <TableBodyCellText variant={'normal'}>{t('commons:amount')}</TableBodyCellText>
                            ),
                            id: 'amount'
                        }
                    ]}
                />
            ) : (
                <DataTable
                    expandedRow={dataItem => <PaymentsExpandedRows assignment={dataItem} />}
                    emptyComponent={<HomeTableEmptySearch onClearSearch={onClearSearch} />}
                    pagination={{
                        ...assignmentsQuery.persistedPagination,
                        currentPage: searchParams.page,
                        limit: TABLE_ELEMENT_LIMIT,
                        onChangePage: page => setSearchParams({page})
                    }}
                    isLoading={assignmentsQuery.isFetching}
                    data={assignmentsQuery.data?.data ?? []}
                    columnPinning={{right: ['cta'], left: ['listing']}}
                    order={{
                        orderBy: assignmentsQuery.sort.sortName ?? undefined,
                        orderDirection: assignmentsQuery.sort.direction ?? undefined
                    }}
                    setOrder={nextOrder =>
                        assignmentsQuery.sort.onSort(nextOrder?.orderBy ?? null, nextOrder?.orderDirection ?? null)
                    }
                    columns={[
                        {
                            cell: ({dataItem}) => <ListingTableCellInfo item={dataItem} />,
                            header: t('commons:listing').toUpperCase(),
                            id: 'listing',
                            size: 220,
                            sortKey: 'title'
                        },
                        {
                            cell: ({dataItem}) => (
                                <TableBodyCellText variant={'dark'}>{dataItem.host.first_name}</TableBodyCellText>
                            ),
                            header: t('home:table:host').toUpperCase(),
                            id: 'host',
                            size: 200,
                            sortKey: 'first_name'
                        },
                        {
                            cell: ({dataItem}) => (
                                <TableBodyCellText variant={'dark'}>
                                    {dataItem.completed_at
                                        ? formatLocaleDate(dataItem.completed_at, 'yyyy-MM-dd')
                                        : '-'}
                                </TableBodyCellText>
                            ),
                            header: t('commons:completed_at').toUpperCase(),
                            id: 'completed_at',
                            size: 200,
                            sortKey: 'completed_at'
                        },
                        {
                            cell: ({dataItem}) => {
                                const isUpworkPayment = dataItem.is_paid_via_upwork
                                if (isUpworkPayment) {
                                    return (
                                        <TableBodyCellText variant={'dark'} className="whitespace-normal">
                                            <Trans i18nKey="assignment:upwork_payment" components={{br: <br />}} />
                                        </TableBodyCellText>
                                    )
                                }
                                return (
                                    <TableBodyCellText variant={'dark'}>
                                        {dataItem.total_amount ? formatCurrency(dataItem.total_amount, 'USD') : '-'}
                                    </TableBodyCellText>
                                )
                            },
                            header: t('commons:amount').toUpperCase(),
                            id: 'amount',
                            size: 180,
                            sortKey: 'amount'
                        },
                        {
                            cell: ({dataItem}) => renderStatusFromAssignment(dataItem),
                            header: t('commons:status').toUpperCase(),
                            id: 'status',
                            size: 200,
                            sortKey: 'status'
                        },
                        {
                            cell: ({dataItem, toggleExpanded, isRowExpanded}) => {
                                const isUpworkPayment = dataItem.is_paid_via_upwork
                                const isCompleted = dataItem.task_status.status == 'completed'
                                const isReview = dataItem.task_category.name == TASK_CATEGORY_REVIEW
                                const isCancelledOrDeclined = CANCELLED_OR_DECLINED_STATUSES.some(
                                    status => status == dataItem.status
                                )
                                const hideOpen = isCancelledOrDeclined || (isCompleted && !isReview)
                                const paymentsCount = dataItem.payments?.length ?? 0

                                if (isUpworkPayment) {
                                    if (isCompleted) {
                                        return (
                                            <ButtonLink
                                                to={UPWORK_URL}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                variant="secondary"
                                                size="sm"
                                            >
                                                {t('assignment:go_to_upwork_cta')}
                                            </ButtonLink>
                                        )
                                    }
                                    return hideOpen ? null : <OpenAssignmentLink code={dataItem.code} search={search} />
                                }

                                if (paymentsCount > 0) {
                                    return (
                                        <Button onClick={toggleExpanded} size={'sm'} variant={'tertiary'}>
                                            {t('home:table:transactions', {count: paymentsCount})}
                                            {isRowExpanded ? (
                                                <ChevronUpIcon size={20} />
                                            ) : (
                                                <ChevronDownIcon size={20} />
                                            )}
                                        </Button>
                                    )
                                }

                                if (hideOpen) {
                                    return null
                                }

                                return <OpenAssignmentLink code={dataItem.code} search={search} />
                            },
                            header: <></>,
                            id: 'cta',
                            size: 160
                        }
                    ]}
                />
            )}
        </div>
    )
}
